import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Link2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, brl, errMsg } from "@/lib/api";

const inp = "w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm";
const COLS = [["produto", "Descrição do produto", "text", "sm:col-span-2"], ["marca_referencia", "Marca", "text", ""], ["modelo", "Modelo / Ref.", "text", ""],
  ["quantidade", "Qtd", "number", ""], ["preco", "Custo unit. (R$)", "number", ""], ["frete", "Frete unit. (R$)", "number", ""], ["prazo", "Prazo", "text", ""]];

const supName = (s) => s.nome_fantasia || s.razao_social || "—";

function cotacaoDoItem(ws, n, supplierId) {
  const cands = (ws.fornecedores || []).filter((f) => String(f.item_numero || "") === n);
  if (supplierId) return cands.find((f) => f.supplier_id === supplierId) || null;
  const sel = (ws.item_fornecimento || {})[n]?.fornecedor_id;
  return cands.find((f) => f.id === sel) || null;
}

function draftFrom(m, cot) {
  return {
    supplier_id: cot?.supplier_id || "", produto: cot?.produto || m.descricao || "",
    marca_referencia: cot?.marca_referencia || "", modelo: cot?.modelo || "",
    quantidade: cot?.quantidade ?? m.quantidade ?? "", preco: cot?.preco ?? "", frete: cot?.frete ?? "", prazo: cot?.prazo || "",
  };
}

function NovoFornecedor({ onClose, onCreated }) {
  const [f, setF] = useState({});
  const save = async () => {
    if (!f.razao_social) { toast.error("Informe a razão social"); return; }
    try { const { data } = await api.post("/suppliers", f); toast.success("Fornecedor cadastrado"); onCreated(data); }
    catch (e) { toast.error(e?.response?.data?.detail?.msg || errMsg(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" data-testid="ws-itcot-novo-forn-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-xl bg-white p-5 shadow-2xl">
        <h3 className="mb-1 font-display text-lg font-bold">Novo fornecedor</h3>
        <p className="mb-3 text-xs text-slate-400">Cadastrado na base geral de Fornecedores (disponível para todas as licitações).</p>
        <div className="grid grid-cols-2 gap-2.5">
          {[["razao_social", "Razão social *", "col-span-2"], ["nome_fantasia", "Nome fantasia", "col-span-2"], ["cnpj", "CNPJ", ""], ["contato", "Contato", ""], ["telefone", "Telefone", ""], ["email", "E-mail", ""]].map(([k, l, c]) => (
            <div key={k} className={c}><label className="mb-0.5 block text-[11px] font-semibold text-slate-600">{l}</label>
              <input data-testid={`ws-itcot-novo-${k}`} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inp} /></div>
          ))}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
          <button data-testid="ws-itcot-novo-save" onClick={save} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">Cadastrar</button>
        </div>
      </div>
    </div>
  );
}

function ItemRow({ m, ws, sups, onSave, savingN }) {
  const n = String(m.numero_item);
  const [d, setD] = useState(() => draftFrom(m, cotacaoDoItem(ws, n)));
  const cot = cotacaoDoItem(ws, n, d.supplier_id);
  const prec = (ws.precificacao?.itens || {})[n] || {};
  const linked = cot && prec.cotacao_id === cot.id;
  const pickSupplier = (sid) => {
    const c = cotacaoDoItem(ws, n, sid);
    setD(c ? draftFrom(m, c) : { ...d, supplier_id: sid });
  };
  return (
    <div data-testid={`ws-itcot-row-${n}`} className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium text-slate-700">Item {n} · <span className="text-slate-500">{m.descricao}</span> <span className="text-xs text-slate-400">({m.quantidade ?? "—"} {m.unidade || ""}{m.valor_unitario ? ` · estimado ${brl(m.valor_unitario)}` : ""})</span></div>
        {linked && <span data-testid={`ws-itcot-linked-${n}`} className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><Link2 className="h-3 w-3" /> Vinculado à Precificação</span>}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5 lg:grid-cols-9">
        <div className="col-span-2">
          <label className="mb-0.5 block text-[10px] font-semibold uppercase text-slate-400">Fornecedor</label>
          <select data-testid={`ws-itcot-${n}-supplier`} value={d.supplier_id} onChange={(e) => pickSupplier(e.target.value)} className={inp}>
            <option value="">Selecione…</option>
            {sups.map((s) => <option key={s.id} value={s.id}>{supName(s)}</option>)}
          </select>
        </div>
        {COLS.map(([k, l, t, c]) => (
          <div key={k} className={c}>
            <label className="mb-0.5 block text-[10px] font-semibold uppercase text-slate-400">{l}</label>
            <input data-testid={`ws-itcot-${n}-${k}`} type={t} value={d[k] ?? ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} className={inp} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-end">
        <button data-testid={`ws-itcot-${n}-save`} onClick={() => onSave(n, d)} disabled={savingN === n} className="flex items-center gap-1 rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">
          {savingN === n && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Salvar cotação do item
        </button>
      </div>
    </div>
  );
}

export const ItensCotacao = ({ id, data, onUpdated }) => {
  const [sups, setSups] = useState([]);
  const [savingN, setSavingN] = useState(null);
  const [novo, setNovo] = useState(false);
  const loadSups = useCallback(async () => {
    try { const { data: s } = await api.get("/suppliers"); setSups(s.filter((x) => (x.status || "ativo") !== "inativo")); }
    catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { loadSups(); }, [loadSups]);

  const save = async (n, d, force = false) => {
    if (!d.supplier_id) { toast.error("Selecione um fornecedor cadastrado"); return; }
    setSavingN(n);
    try {
      const { data: r } = await api.put(`/opportunities/${id}/itens/${n}/cotacao`, { ...d, force });
      onUpdated(r); toast.success(`Item ${n}: cotação salva e Precificação atualizada`);
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.conflitos) {
        const txt = det.conflitos.map((c) => `${c.campo === "frete" ? "Frete" : "Custo"}: ${brl(c.atual)} → ${brl(c.novo)}`).join("\n");
        if (window.confirm(`${det.msg}\n\n${txt}\n\nSubstituir os valores da Precificação pelos da cotação?`)) { setSavingN(null); return save(n, d, true); }
        toast.info("Nada alterado — valores da Precificação preservados");
      } else toast.error(errMsg(e));
    } finally { setSavingN(null); }
  };

  const items = data.item_matches || [];
  return (
    <div data-testid="ws-itens-cotacao" className="mb-6">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-bold text-slate-800">Itens da licitação</div>
          <p className="text-xs text-slate-500">Selecione o fornecedor cadastrado e informe os custos uma única vez — custo e frete atualizam a Precificação automaticamente.</p>
        </div>
        <button data-testid="ws-itcot-novo-forn" onClick={() => setNovo(true)} className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"><Plus className="h-3.5 w-3.5" /> Novo fornecedor</button>
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">Nenhum item carregado.</div>
      ) : (
        <div className="space-y-2">{items.map((m) => <ItemRow key={m.numero_item} m={m} ws={data.workspace} sups={sups} onSave={save} savingN={savingN} />)}</div>
      )}
      {novo && <NovoFornecedor onClose={() => setNovo(false)} onCreated={(s) => { setNovo(false); setSups((x) => [s, ...x]); }} />}
    </div>
  );
};
